import React, { useEffect, useRef } from "react";

type PixelStarCanvasProps = {
  grid?: number; // squares per side
  stepMs?: number; // time to wait on a tile before filling it
  travelMs?: number; // ring travel duration between tiles
  groupPauseMs?: number; // pause between groups (1..6)
  pauseMs?: number; // pause after full star before reset
  baseGrey?: string; // unfilled color
  selectedGrey?: string; // selected unfilled color
  goldShades?: string[]; // at least 6 colors (one per group 1..6)
  background?: string; // background color
  showNumbers?: boolean; // draw 1..6 on unfilled tiles
  ringPx?: number; // ring thickness in device pixels
  ringScale?: number; // ring diameter relative to cell (0..1)
};

export const PixelLoader: React.FC<PixelStarCanvasProps> = ({
  grid = 21,
  stepMs = 10,
  travelMs = 18,
  groupPauseMs = 50,
  pauseMs = 45,
  baseGrey = "#d3d8e3",
  selectedGrey = "#b0b7c3",
  goldShades = [
    "#FFCC33",
    "#FFC233",
    "#FFD65C",
    "#FFE08A",
    "#FFB300",
    "#FFE699",
  ],
  background = "#ffffff",
  showNumbers = true,
  ringPx = 3,
  ringScale = 2,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const size = 250;

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const dpr = 1; // Math.max(1, window.devicePixelRatio || 1);
    // ---- palette geometry (device pixels) ----
    const PAL_COUNT = 6;
    const palGap = Math.round(10 * dpr);
    const palSize = Math.round(16 * dpr); // square swatch size
    const palRadius = Math.round(6 * dpr); // set to 0 for sharp corners
    const palY = size - Math.round(18 * dpr) - palSize; // bottom margin
    let palX0 = 0; // left-most x; computed per frame to keep centered

    function roundRectPath(
      x: number,
      y: number,
      w: number,
      h: number,
      r: number
    ) {
      const rr = Math.min(r, Math.min(w, h) / 2);
      ctx.beginPath();
      ctx.moveTo(x + rr, y);
      ctx.lineTo(x + w - rr, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
      ctx.lineTo(x + w, y + h - rr);
      ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
      ctx.lineTo(x + rr, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
      ctx.lineTo(x, y + rr);
      ctx.quadraticCurveTo(x, y, x + rr, y);
      ctx.closePath();
    }

    function drawPalette(currentGroup: number) {
      // center the 6 swatches
      const totalW = PAL_COUNT * palSize + (PAL_COUNT - 1) * palGap;
      palX0 = Math.round((canvas.width - totalW) / 2);

      for (let i = 0; i < PAL_COUNT; i++) {
        const x = palX0 + i * (palSize + palGap);
        const y = palY;
        const color = goldShades[i % goldShades.length] || goldShades[0];

        // check if this group's tiles are all filled
        const groupIndex = i; // 0..5 for groups 1..6
        const done = groups[groupIndex].every((ci) => cells[ci].filled);
        const isCurrent = i + 1 === currentGroup;

        // base swatch
        ctx.save();
        roundRectPath(x, y, palSize, palSize, palRadius);
        ctx.fillStyle = color;
        ctx.fill();

        // subtle inset border
        ctx.lineWidth = Math.max(1, Math.floor(2 * dpr));
        ctx.strokeStyle = "rgba(0,0,0,0.12)";
        ctx.stroke();
        ctx.restore();

        // highlight the current group
        if (isCurrent) {
          const glow = Math.max(3, Math.floor(3 * dpr));
          ctx.save();
          ctx.lineWidth = Math.max(1, Math.floor(3 * dpr));
          ctx.strokeStyle = "rgba(255,255,255,0.95)";
          roundRectPath(
            x - glow,
            y - glow,
            palSize + glow * 2,
            palSize + glow * 2,
            palRadius + glow
          );
          ctx.stroke();
          ctx.shadowColor = "rgba(0,0,0,0.25)";
          ctx.shadowBlur = Math.floor(6 * dpr);
          ctx.shadowOffsetY = Math.floor(2 * dpr);
          roundRectPath(x, y, palSize, palSize, palRadius);
          ctx.strokeStyle = "rgba(0,0,0,0.08)";
          ctx.stroke();
          ctx.restore();
        }

        // draw number or tick
        ctx.save();
        const cx = x + palSize / 2;
        const cy = y + palSize / 2;

        if (done) {
          // ✓ tick
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = Math.max(2, Math.floor(3 * dpr));
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          // simple tick path sized to the swatch
          const s = palSize * 0.42;
          ctx.beginPath();
          ctx.moveTo(cx - s * 0.6, cy + s * 0.05);
          ctx.lineTo(cx - s * 0.1, cy + s * 0.5);
          ctx.lineTo(cx + s * 0.65, cy - s * 0.45);
          ctx.stroke();
        } else {
          // centered number (1..6)
          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = `${Math.floor(
            palSize * 0.6
          )}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto`;
          // stronger contrast for current, slightly dim for others
          ctx.globalAlpha = isCurrent ? 1 : 0.9;
          ctx.fillText(String(i + 1), cx, cy + Math.floor(0.02 * palSize));
          ctx.globalAlpha = 1;
        }
        ctx.restore();
      }
    }

    // --- sizing: full-viewport square ---
    const side = size; // .min(window.innerWidth, window.innerHeight);
    canvas.style.width = `${side}px`;
    canvas.style.height = `${side}px`;
    canvas.width = Math.floor(side * dpr);
    canvas.height = Math.floor(side * dpr);

    // cell geometry
    const cell = Math.floor(canvas.width / grid);
    const offset = Math.floor((canvas.width - cell * grid) / 2);

    // --- star polygon in normalized space ---
    const starPoly: Array<{ x: number; y: number }> = [];
    const outerR = 0.46,
      innerR = 0.2,
      cx = 0.5,
      cy = 0.5;
    for (let k = 0; k < 10; k++) {
      const ang = -Math.PI / 2 + (k * Math.PI) / 5;
      const r = k % 2 === 0 ? outerR : innerR;
      starPoly.push({ x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang) });
    }
    const pip = (x: number, y: number) => {
      let inside = false;
      for (let i = 0, j = starPoly.length - 1; i < starPoly.length; j = i++) {
        const xi = starPoly[i].x,
          yi = starPoly[i].y;
        const xj = starPoly[j].x,
          yj = starPoly[j].y;
        const inter =
          yi > y !== yj > y &&
          x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-9) + xi;
        if (inter) inside = !inside;
      }
      return inside;
    };

    // Build star cells in serpentine order
    type Cell = {
      x: number;
      y: number;
      cx: number;
      cy: number;
      group: number;
      filled: boolean;
    };
    const cells: Cell[] = [];
    for (let gy = 0; gy < grid; gy++) {
      const row: Cell[] = [];
      for (let gx = 0; gx < grid; gx++) {
        const px = (gx + 0.5) / grid;
        const py = (gy + 0.5) / grid;
        if (pip(px, py)) {
          const cxp = offset + gx * cell + cell / 2;
          const cyp = offset + gy * cell + cell / 2;
          row.push({ x: gx, y: gy, cx: cxp, cy: cyp, group: 0, filled: false });
        }
      }
      if (gy % 2 === 1) row.reverse();
      cells.push(...row);
    }

    // Assign groups 1..6 evenly by serpentine index
    for (let i = 0; i < cells.length; i++) {
      cells[i].group = (i % 6) + 1;
    }

    // Precompute group index lists
    const groups: number[][] = Array.from({ length: 6 }, () => []);
    cells.forEach((c, i) => groups[c.group - 1].push(i));

    // --- helpers ---
    const drawFrame = (ringX?: number, ringY?: number, ringColor?: string) => {
      // bg
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // text setup
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${Math.floor(
        cell * 0.55
      )}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto`;

      for (let i = 0; i < cells.length; i++) {
        const c = cells[i];

        // 1) filled tiles = gold
        if (c.filled) {
          ctx.fillStyle =
            goldShades[(c.group - 1) % goldShades.length] || goldShades[0];
        }
        // 2) UNfilled tiles that match the CURRENT group = selected grey
        else if (c.group === nowGroup) {
          ctx.fillStyle = selectedGrey;
        }
        // 3) other unfilled tiles = base grey
        else {
          ctx.fillStyle = baseGrey;
        }

        ctx.fillRect(offset + c.x * cell, offset + c.y * cell, cell, cell);

        // numbers on unfilled tiles
        if (showNumbers && !c.filled) {
          // optional: make the active group's numbers a bit darker for contrast
          ctx.fillStyle =
            c.group === nowGroup ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0.55)";
          ctx.fillText(String(c.group), c.cx, c.cy);
        }
      }

      // ring
      if (ringX !== undefined && ringY !== undefined && ringColor) {
        ctx.save();
        ctx.lineWidth = ringPx * dpr;
        ctx.strokeStyle = ringColor;
        const r = (cell * ringScale) / 2;
        ctx.beginPath();
        ctx.arc(ringX, ringY, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      drawPalette(nowGroup);
    };

    const colorForGroup = (g: number) =>
      goldShades[(g - 1) % goldShades.length] || goldShades[0];

    // --- animation state ---
    let nowGroup = 1; // 1..6
    let idxInGroup = 0; // index within groups[nowGroup-1]
    let phase: "travel" | "hold" | "betweenGroups" | "pause" = "travel";

    // ring travel state
    let fromX = 0,
      fromY = 0,
      toX = 0,
      toY = 0;
    let travelStart = performance.now();
    let holdStart = performance.now();

    // initialize ring start at first cell center
    if (groups[0].length > 0) {
      const firstIdx = groups[0][0];
      fromX = toX = cells[firstIdx].cx;
      fromY = toY = cells[firstIdx].cy;
    }

    const startTravelTo = (cellIndex: number, startTime: number) => {
      fromX = toX;
      fromY = toY;
      toX = cells[cellIndex].cx;
      toY = cells[cellIndex].cy;
      travelStart = startTime;
      phase = "travel";
    };

    const startHold = (startTime: number) => {
      holdStart = startTime;
      phase = "hold";
    };

    const nextTarget = (t: number) => {
      const arr = groups[nowGroup - 1];
      if (idxInGroup < arr.length) {
        startTravelTo(arr[idxInGroup], t);
      } else {
        phase = "betweenGroups";
        holdStart = t; // reuse as group pause start
      }
    };

    // --- main loop ---
    const loop = (t: number) => {
      let ringX = toX,
        ringY = toY;
      const ringColor = colorForGroup(nowGroup);

      if (phase === "travel") {
        const u = Math.min(1, (t - travelStart) / travelMs);
        // simple linear tween (could ease if desired)
        ringX = fromX + (toX - fromX) * u;
        ringY = fromY + (toY - fromY) * u;

        if (u >= 1) {
          // Arrived: start hold phase
          startHold(t);
        }
      } else if (phase === "hold") {
        ringX = toX;
        ringY = toY;
        if (t - holdStart >= stepMs) {
          // Fill current tile
          const arr = groups[nowGroup - 1];
          const fillIdx = arr[idxInGroup];
          if (fillIdx !== undefined) cells[fillIdx].filled = true;
          idxInGroup++;
          // Move to next tile in same group (or transition to betweenGroups)
          nextTarget(t);
        }
      } else if (phase === "betweenGroups") {
        ringX = toX;
        ringY = toY;
        if (t - holdStart >= groupPauseMs) {
          nowGroup++;
          idxInGroup = 0;
          if (nowGroup <= 6) {
            // Change ring color automatically (colorForGroup uses nowGroup)
            nextTarget(t);
          } else {
            phase = "pause";
            holdStart = t;
          }
        }
      } else if (phase === "pause") {
        // pause at the end
        if (t - holdStart >= pauseMs) {
          // reset all
          for (const c of cells) c.filled = false;
          nowGroup = 1;
          idxInGroup = 0;
          // Ring starts from last position, heads to first of group 1
          nextTarget(t);
        }
      }

      // draw current frame
      drawFrame(ringX, ringY, ringColor);

      rafRef.current = requestAnimationFrame(loop);
    };

    // prime first target and start
    nextTarget(performance.now());
    rafRef.current = requestAnimationFrame(loop);

    // cleanup
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        zIndex: 100,
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        background,
      }}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} />
    </div>
  );
};
